import { defineStore } from "pinia";
import { kc } from "@/main";
import router from "@/router";

// Const
import config from "@/config/env/index";
import { organizationAuth } from "@/const/auth";
// Services
import userApiService from "@/services/apiBISOM/users";
import userPrefApiService from "@/services/apiBISOM/userPref";
import notificationService from "@/services/notification";
// Types
import type { UserDivision } from "@/types/userDivsionTypes";
import type { OrganizationType } from "@/types/organizationTypes";
import type { UserPref } from "@/types/userPref";
import type { OrgaAuth } from "@/types/organizationAuth";
type ExtractOrgaAuth<T extends OrgaAuth["param_key"]> = Extract<OrgaAuth, { param_key: T }>;
// Utils
import { getItemLocalStorage, setItemLocalStorage } from "@/utils/localStorage";
import { freshworksAuthenticate } from "@/utils/freshworksAuth";

export const useUserInfosStore = defineStore("userInfosStore", {
  state: () => ({
    userInfos: {
      admin_auth: [] as number[],
      divisions: [] as { id: number; name: string }[],
      job_scope: "",
      freshdesk_api_key: "" as string,
      freshworks_widget_auth_token: "" as string,
      organization_auth: [] as OrgaAuth[],
      organization_location: { longitude: 0, latitude: 0 },
      organizations: [] as { id: number; name: string }[],
      user_name: "",
      user_id: "",
      user_organization: "",
      user_function_id: NaN as number,
      user_auth: [] as number[],
      managed_level: null as number | null,
      dispatch_system: null as number | null,
    },
    userDivisions: [] as UserDivision[],
    currentUserDivision: {} as { latitude: number; longitude: number },
    userManagedOrganizations: [] as OrganizationType[],
    userMail: "",
    versionApp: process.env.VUE_APP_VERSION ?? "",
    isMobile: screen.width <= 760 ? true : false,
    isFullscreen: false,
    userPref: {} as UserPref,
  }),

  getters: {
    username(state) {
      return state.userInfos.user_name;
    },
    userOrga(state) {
      return state.userInfos.user_organization;
    },
    userId(state) {
      return state.userInfos.user_id;
    },

    userAuths(state) {
      return state.userInfos.user_auth;
    },

    userOrganizationId(state): number {
      return state.userInfos.organizations[0]?.id ?? 0;
    },

    userDivisionId(state): number {
      return state.userInfos.divisions[0]?.id ?? 0;
    },
    userDivisionLocation(state) {
      const division = state.currentUserDivision;
      return division.latitude ? [division.latitude, division.longitude] : [];
    },

    userOrganizationAuth(state) {
      return state.userInfos.organization_auth;
    },

    userFunctionId(state) {
      return state.userInfos.user_function_id;
    },

    userJobscope(state) {
      return state.userInfos.job_scope.toLocaleUpperCase();
    },

    isLight(state) {
      const theme = state.userPref.screen?.theme_light;

      return theme === undefined ? true : theme;
    },
    theme(state) {
      let theme = state.userPref.screen?.theme_light;
      theme = theme === undefined ? true : theme;

      return theme ? "light" : "dark";
    },
    lrmScan(state) {
      const orgaAuthScan = state.userInfos.organization_auth.find(
        (auth) => auth.param_key === organizationAuth.LRM_SCAN
      );
      return {
        type: orgaAuthScan?.val1,
        third_party_id: orgaAuthScan?.int1,
      };
    },

    bistriEnv(state) {
      return {
        appId: config.bistri_env.app_id,
        appKey: config.bistri_env.app_key,
        debug: config.bistri_env.debug_mode,
        userName: state.userInfos.user_organization,
      };
    },
  },

  actions: {
    getApiV3Bisom(
      isDivision?: boolean,
      divisionId?: number,
      organizationId?: number,
      apiUrl?: string
    ) {
      const orgaId = organizationId ?? this.userOrganizationId;
      const apiv3 = apiUrl
        ? `${apiUrl}api/v1/organizations/${orgaId}` // archivage
        : `${config.api_bisom_url}api/v3/organizations/${orgaId}`;
      return !isDivision ? apiv3 : `${apiv3}/divisions/${divisionId ?? this.userDivisionId}`;
    },

    async getUserInfos() {
      try {
        if (!this.userInfos.user_name) {
          const tokenParsed = kc.idTokenParsed;
          this.userMail = tokenParsed?.email;
          localStorage.removeItem("user_divisions");
          localStorage.removeItem("user_organizations");

          this.userInfos = await userApiService.getUserInfos();

          freshworksAuthenticate(this.userInfos.freshworks_widget_auth_token);
          await this.getUserDivision();

          this.getUserPref();
          await this.getUserManagedOrganizations();
        }

        return Promise.resolve(this.userInfos);
      } catch (error) {
        notificationService.notifyError(error);
        if ((error as { response: { data: { error: number } } })?.response?.data?.error === 162) {
          router.push({ name: "wrong_ip_addres" });
        }

        return Promise.reject(error);
      }
    },

    async getUserDivision() {
      try {
        const userDivisionsFromStorage = getItemLocalStorage("user_divisions");
        if (userDivisionsFromStorage) {
          this.userDivisions = userDivisionsFromStorage;
        } else {
          this.userDivisions = await userApiService.fetchUserDivisions();
          setItemLocalStorage("user_divisions", this.userDivisions);
        }

        return Promise.resolve(this.userDivisions);
      } catch (error) {
        notificationService.notifyError(error);
        return Promise.reject(error);
      }
    },

    async getUserManagedOrganizations() {
      try {
        const userOrganizationsFromStorage = getItemLocalStorage("user_organizations");
        if (userOrganizationsFromStorage) {
          this.userManagedOrganizations = userOrganizationsFromStorage;
        } else {
          this.userManagedOrganizations = await userApiService.fetchUserManagedOrganizations();

          setItemLocalStorage("user_organizations", this.userManagedOrganizations);
        }

        return Promise.resolve(this.userManagedOrganizations);
      } catch (error) {
        notificationService.notifyError(error);
        return Promise.reject(error);
      }
    },

    updateTheme() {
      this.userPref = {
        ...this.userPref,
        screen: { theme_light: !this.isLight },
      };

      this.postUserPref();
    },

    hasUserAuth(auth: number) {
      return this.userInfos.user_auth.includes(auth);
    },

    async getUserPref() {
      try {
        if (!this.userPref.screen) {
          const res = await userPrefApiService.getUserPref();
          if (res.setting) {
            if (!res.setting?.map) {
              res.setting.map = {
                operator: [],
                inter: false,
                hospital: false,
                base: false,
                satellite: false,
                auto_focus: true,
              };
            } else if (!res.setting?.map?.operator?.length) {
              res.setting.map.operator = [];
            }
            this.userPref = res.setting;
          } else {
            this.userPref = {
              screen: {
                theme_light: true,
              },
              map: {
                operator: [],
                inter: false,
                hospital: false,
                base: false,
                satellite: false,
                auto_focus: true,
              },
            };
            this.postUserPref();
          }
        }

        return Promise.resolve(this.userPref);
      } catch (error) {
        notificationService.notifyError(error);
        return Promise.reject(error);
      }
    },

    async postUserPref() {
      try {
        const body = {
          setting: this.userPref,
        };
        await userPrefApiService.postUserPref(body);

        return Promise.resolve(this.userPref);
      } catch (error) {
        notificationService.notifyError(error);
        return Promise.reject(error);
      }
    },

    findOrgaAuth<T extends OrgaAuth["param_key"]>(key: T): ExtractOrgaAuth<T> | undefined {
      return this.userOrganizationAuth.find((el): el is ExtractOrgaAuth<T> => el.param_key === key);
    },
  },
});
